import { watchSignIn } from 'utils/signedOut';
import { useCallback, useEffect, useRef, useState } from 'react';

/** What the corner of the screen is saying. */
export type AutoSaveState =
  | 'idle'
  | 'saving'
  /**
   * One store has been on its way for SLOW_AFTER_MS and has no answer yet:
   * "Still saving…" (Steven, 6 Oct 2026). It still gets SAVE_TIME_LIMIT_MS
   * before it counts as failed.
   */
  | 'saving-slow'
  | 'saved'
  | 'failing'
  /** The server refused the save: the screen is out of date (D10). */
  | 'refused';

/** Who changed the thing since this screen was built, and when. */
export interface RefusalInfo {
  /** "Anna P." — null when the server does not know. */
  by: string | null;
  /** The same person, on another screen (another tab). */
  own: boolean;
  at: string | null;
  /** The count the server holds now. */
  keptVersion: number;
}

/**
 * The save was REFUSED, not failed: somebody changed the thing since this
 * screen was built (D10, scene 14). Never retried by itself: the person
 * chooses between their version and the other one.
 */
export class SaveRefused extends Error {
  constructor(public readonly info: RefusalInfo) {
    super('The screen is out of date.');
    this.name = 'SaveRefused';
  }
}

export interface SaveOutcome {
  /** When the server says it kept the work. */
  keptAt?: string;
  /** What was left out, in the words the description fixes. */
  note?: string;
}

/** D24: the work is sent about two seconds after the person stops. */
export const QUIET_MS = 2000;
/** D8/5b item 2: the app keeps trying by itself, at GROWING intervals. */
export const RETRY_FIRST_MS = 2000;
export const RETRY_CEILING_MS = 60000;

/** 2s, 4s, 8s … up to a minute, so a long outage does not hammer the server. */
export const retryDelay = (attempt: number): number =>
  Math.min(RETRY_FIRST_MS * 2 ** Math.max(0, attempt - 1), RETRY_CEILING_MS);

interface UseAutoSaveArgs<T> {
  /** The screen's current state. Any change to it starts the clock. */
  value: T | undefined;
  /**
   * Stores the work. Returns the moment the SERVER says it was kept, and a note
   * when part of the screen was deliberately left out (5b item 4).
   */
  /**
   * Stores the state. `signal` is given up when the save has taken too long
   * or the person chose to lose the work: a save that honours it cannot
   * arrive later on top of newer words.
   */
  save: (
    value: T,
    signal?: AbortSignal
  ) => Promise<SaveOutcome | string | void>;
  /** Off while the screen is still loading, or on a screen that must not save. */
  enabled?: boolean;
  /**
   * WHAT this screen is editing -- the campaign, here.
   *
   * Picking another campaign does not change the address and does not rebuild
   * the screen (NavigationContext.tsx:331-349), so without this the baseline
   * and the pending work belong to the campaign the person has LEFT, and the
   * next save writes its words onto the new one.
   */
  identity?: string | number | null;
  /**
   * When the server last kept this screen, as it arrived with the page. 5b item
   * 3: opening a screen already shows "Saved at" with that time, instead of an
   * empty corner until the person types something.
   */
  keptAtOnOpen?: string | null;
  /**
   * Sent instead of `save` when the tab is going away, because a normal
   * request does not survive that.
   */
  flushOnHide?: (value: T) => void;
  /**
   * Another thing is opened while work on the previous one is still inside the
   * pause. Called with the PREVIOUS identity and its last state, so the work
   * is sent where it belongs. Without it that work was simply dropped: typing
   * and picking another campaign within two seconds lost the typing (review
   * round 5, C7).
   */
  flushOnIdentityChange?: (
    previousIdentity: string | number | null,
    value: T
  ) => void;
}

interface UseAutoSaveResult {
  state: AutoSaveState;
  /** The moment the server kept the work, for "Saved at 14:02". */
  keptAt: Date | null;
  /** Set when part of the work was deliberately not sent. */
  note: string | undefined;
  /** True while something typed is not stored yet, including while retrying. */
  hasPending: boolean;
  /**
   * A save has failed and has not succeeded since. Stays true THROUGH the
   * retries, which each set the state to "saving": the leave question has to
   * stay armed for the whole outage, not blink off every few seconds.
   */
  hasFailed: boolean;
  /** Store now, without waiting out the pause. */
  flush: () => Promise<void>;
  /**
   * Store now, the ordinary way, and answer: is everything kept?
   *
   * For leaving to another campaign (Steven, 3 Oct 2026): the switch waits for
   * this. It lets a save that is already in the air finish first, so nothing
   * is sent twice, and it gives up after a while rather than hold the person
   * on a dead line -- "not kept" then, and the leave question is asked.
   */
  saveNow: () => Promise<boolean>;
  /** Right now: nothing waiting, nothing in the air. Asks nothing, sends nothing. */
  isKept: () => boolean;
  /**
   * Throw the pending work away, deliberately. "Leave and lose changes" means
   * exactly that: without this the screen is torn down still counting the work
   * as outstanding, and the send-on-the-way-out posts the very words the person
   * chose to abandon.
   */
  abandon: () => void;
  /** Set while a save was refused and the person has not chosen yet. */
  refusal: RefusalInfo | null;
  /** "Keep mine": the refused save goes again (the caller marks it so). */
  retryRefused: () => Promise<void>;
}

/**
 * `saveNow` was waiting for an answer nobody needs any more: the person chose
 * to lose the work meanwhile. Whoever waited must drop what it was about to do.
 */
export class SaveNowMoot extends Error {
  constructor() {
    super('The work was given up while the save was awaited.');
    this.name = 'SaveNowMoot';
  }
}

/**
 * Everything sent by this hook, by whatever road, numbered, per thing it was
 * sent for; and which things are open on screen right now.
 *
 * Kept OUTSIDE the hook on purpose: it has to outlive one opening of the
 * screen. A failed save of a thing that was left is handed on only if nothing
 * else has gone out for that thing since it started and the thing is not open
 * again. Counted per opening, a save from a screen that had been closed knew
 * nothing of the newer words stored by the screen opened after it, and sent
 * its older words on top (review round 10, shown by experiment).
 */
type Thing = string | number | null;
const sends = { count: 0, lastFor: new Map<Thing, number>() };
const openNow = new Map<Thing, number>();
const noteSendFor = (thing: Thing): number => {
  sends.count += 1;
  sends.lastFor.set(thing, sends.count);
  return sends.count;
};
const isOpenNow = (thing: Thing): boolean => (openNow.get(thing) ?? 0) > 0;

export const IS_SAFARI =
  typeof navigator !== 'undefined' &&
  /safari/i.test(navigator.userAgent) &&
  !/chrome|chromium|crios|fxios|android|edg/i.test(navigator.userAgent);

/**
 * How long one save request may take. A save that hangs is stopped and counts
 * as failed, so it is tried again and can no longer arrive minutes later on
 * top of newer words. Claude's choice; on Steven's list of questions.
 */
export const SAVE_TIME_LIMIT_MS = 20_000;
/** After this long without an answer the corner says "Still saving…". */
export const SLOW_AFTER_MS = 5_000;

/** How long a campaign switch waits for the save before it asks instead. */
export const SAVE_NOW_PATIENCE_MS = 8000;
/** How often it tries, and how long it waits between tries. */
export const SAVE_NOW_ATTEMPTS = 3;
export const SAVE_NOW_RETRY_GAP_MS = 1000;

const fingerprint = (value: unknown): string =>
  value === undefined ? '' : JSON.stringify(value);

/**
 * Keeps a screen's work without a Save button (scenes 1, 3, 5, 12).
 *
 * Deliberately NOT a copy on the person's own computer (D9): the only place the
 * work lives is the server, so everything here is about getting it there and
 * telling the truth about whether it arrived.
 */
export const useAutoSave = <T>({
  value,
  save,
  enabled = true,
  identity = null,
  keptAtOnOpen,
  flushOnHide,
  flushOnIdentityChange,
}: UseAutoSaveArgs<T>): UseAutoSaveResult => {
  const [state, setState] = useState<AutoSaveState>('idle');
  const [keptAt, setKeptAt] = useState<Date | null>(
    keptAtOnOpen ? new Date(keptAtOnOpen) : null
  );
  const [note, setNote] = useState<string | undefined>(undefined);
  const [hasPending, setHasPending] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [refusal, setRefusal] = useState<RefusalInfo | null>(null);
  /** 5b item 16: while refused, nothing more is saved until the choice. */
  const refusedRef = useRef(false);
  const clearRefusal = () => {
    refusedRef.current = false;
    setRefusal(null);
  };

  const valueRef = useRef<T | undefined>(value);
  /** The last state the server confirmed. Null until the screen has loaded. */
  const keptPrint = useRef<string | null>(null);
  const quietTimer = useRef<number | undefined>(undefined);
  const retryTimer = useRef<number | undefined>(undefined);
  const inFlight = useRef(false);
  const pendingRef = useRef(false);
  /** `hasFailed`, readable at once by code that cannot wait for a render. */
  const failedRef = useRef(false);
  /** The words of the save that is in the air, to tell them from newer ones. */
  const inFlightPrint = useRef<string | null>(null);
  const flushSentRef = useRef(false);
  /** The words the quiet clock was last started for. */
  const scheduledPrint = useRef<string | null>(null);
  const attempts = useRef(0);
  /** Kept in a ref so the unmount cleanup below can reach the newest one. */
  const flushRef = useRef(flushOnHide);
  flushRef.current = flushOnHide;

  valueRef.current = value;

  /** The last state seen, with the thing it was the state OF. */
  const lastSeenRef = useRef<{
    identity: string | number | null;
    value: T;
  } | null>(null);
  if (value !== undefined) lastSeenRef.current = { identity, value };
  const flushIdentityRef = useRef(flushOnIdentityChange);
  flushIdentityRef.current = flushOnIdentityChange;
  const saveRef = useRef(save);
  saveRef.current = save;

  // Scene 13: while this screen is open the sign-in is looked at, and the
  // login screen can say since when changes were not saved.
  const signIn = useRef<ReturnType<typeof watchSignIn> | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const watch = watchSignIn();
    signIn.current = watch;
    return () => {
      signIn.current = null;
      watch.closed();
    };
  }, [enabled]);
  useEffect(() => {
    signIn.current?.kept(keptAt);
  }, [keptAt, enabled]);
  useEffect(() => {
    signIn.current?.pending(hasPending);
  }, [hasPending, enabled]);

  useEffect(() => {
    openNow.set(identity, (openNow.get(identity) ?? 0) + 1);
    return () => {
      openNow.set(identity, Math.max(0, (openNow.get(identity) ?? 1) - 1));
    };
  }, [identity]);

  /** The request of the save that is in the air, so it can be stopped. */
  const inFlightRequest = useRef<AbortController | null>(null);

  /*
   * The era a save belongs to. It ends when another thing is opened and when
   * the person chooses to lose the work. The answer of a save from an ended
   * era is IGNORED, whole: no baseline, no corner, and above all no retry.
   *
   * Review round 7 (3 Oct 2026) showed why, by experiment: a save still in the
   * air at "Leave and lose changes" that then failed booked a retry, and the
   * retry sent the page of the campaign just opened to the campaign that was
   * left.
   */
  const era = useRef(0);
  /**
   * Eras whose failed save must NOT be handed to the last resort: the person
   * chose to lose that work, or newer words were already sent for it.
   */
  const noHandOver = useRef<Set<number>>(new Set());
  /** Counts "Leave and lose changes", for a `saveNow` that was waiting. */
  const abandoned = useRef(0);
  const unmounted = useRef(false);

  /*
   * A different thing is being edited now. Everything the hook believes is
   * about the previous one, so it all goes: the baseline, the pending work, the
   * timers and the right to send anything on the way out.
   */
  const identityRef = useRef(identity);
  useEffect(() => {
    if (identityRef.current === identity) return;
    const previous = identityRef.current;
    identityRef.current = identity;

    /*
     * A LAST RESORT. The campaign picker waits for `saveNow` before it
     * switches, so there is normally nothing waiting here. This catches the
     * other ways the campaign can change under an open job page. It is sent
     * blind (nobody reads the answer), so it is never sent beside a save that
     * already carries the SAME words (review round 6, E3). Newer words than
     * the ones in the air are sent: skipping those lost them (round 7, G2).
     */
    const lastSeen = lastSeenRef.current;
    const sameWordsAreInTheAir =
      inFlight.current &&
      !!lastSeen &&
      fingerprint(lastSeen.value) === inFlightPrint.current;
    if (
      pendingRef.current &&
      !flushSentRef.current &&
      !sameWordsAreInTheAir &&
      // Refused words are not sent on the way out either (check 4.4).
      !refusedRef.current &&
      lastSeen &&
      lastSeen.identity === previous
    ) {
      noteSendFor(previous);
      flushIdentityRef.current?.(previous, lastSeen.value);
      // Newer words are on their way: an older save failing later must not
      // be sent after them.
      noHandOver.current.add(era.current);
    }
    if (lastSeen && lastSeen.identity !== identity) lastSeenRef.current = null;

    era.current += 1;
    inFlight.current = false;

    window.clearTimeout(quietTimer.current);
    window.clearTimeout(retryTimer.current);
    keptPrint.current = null;
    flushSentRef.current = true;
    attempts.current = 0;
    pendingRef.current = false;
    setHasPending(false);
    failedRef.current = false;
    setHasFailed(false);
    setState('idle');
    setNote(undefined);
    setKeptAt(null);
    clearRefusal();
  }, [identity]);

  // The page's own last-save time arrives after the first render.
  useEffect(() => {
    if (!keptAtOnOpen) return;
    setKeptAt((current) => current ?? new Date(keptAtOnOpen));
  }, [keptAtOnOpen]);

  const setPending = (pending: boolean) => {
    pendingRef.current = pending;
    setHasPending(pending);
  };

  const runSave = useCallback(async (): Promise<void> => {
    const current = valueRef.current;
    if (current === undefined) return;
    if (inFlight.current) return;
    if (refusedRef.current) return;

    const print = fingerprint(current);
    const mine = era.current;
    const mineIdentity = identityRef.current;
    const mineSend = noteSendFor(mineIdentity);
    inFlight.current = true;
    inFlightPrint.current = print;
    setState('saving');

    const request = new AbortController();
    inFlightRequest.current = request;
    const tooLong = window.setTimeout(
      () => request.abort(),
      SAVE_TIME_LIMIT_MS
    );
    // Only for THIS store: typing on while stores come and go is not slow.
    const slow = window.setTimeout(() => {
      if (era.current === mine && inFlightRequest.current === request)
        setState((now) => (now === 'saving' ? 'saving-slow' : now));
    }, SLOW_AFTER_MS);

    try {
      // Always the save of the thing that is open NOW, never one remembered
      // from an earlier render: a timer set before a campaign change must not
      // be able to send to the campaign that was left.
      const outcome = await saveRef.current(current, request.signal);
      if (era.current !== mine) return;
      const serverKeptAt =
        typeof outcome === 'string' ? outcome : outcome?.keptAt;
      setNote(typeof outcome === 'string' ? undefined : outcome?.note);
      keptPrint.current = print;
      setKeptAt(serverKeptAt ? new Date(serverKeptAt) : new Date());
      attempts.current = 0;
      failedRef.current = false;
      setHasFailed(false);
      window.clearTimeout(retryTimer.current);

      // Typing may have continued while the request was in the air. Saying
      // "Saved at" now would claim the NEWER words are stored too, which
      // done-point 7 forbids, so the line keeps saying a save is under way.
      const stillDirty = fingerprint(valueRef.current) !== keptPrint.current;
      setPending(stillDirty);
      setState(stillDirty ? 'saving' : 'saved');
      if (stillDirty) {
        window.clearTimeout(quietTimer.current);
        quietTimer.current = window.setTimeout(() => void runSave(), QUIET_MS);
      }
    } catch (_e) {
      if (era.current !== mine && _e instanceof SaveRefused) {
        // Refused after the thing was left: it is never sent again, least of
        // all without its count, which would store it blind.
        return;
      }
      if (era.current !== mine) {
        /*
         * The thing was left while this save was in the air, and now it has
         * failed. Nothing else will send those words: the last resort was
         * skipped because they were already travelling. So it is sent now,
         * to the thing they belong to (review round 8: this was a silent
         * loss). Not if the person chose to lose them; not if anything else
         * went out for it meanwhile, by any road; and not if it is open again.
         */
        const nothingSentForItSince =
          sends.lastFor.get(mineIdentity) === mineSend;
        // Back on that very thing: the screen now shows what the server
        // holds, and the person works from there.
        const itIsOpenAgain = isOpenNow(mineIdentity);
        if (
          !noHandOver.current.has(mine) &&
          nothingSentForItSince &&
          !itIsOpenAgain
        ) {
          noteSendFor(mineIdentity);
          flushIdentityRef.current?.(mineIdentity, current);
        }
        return;
      }
      if (_e instanceof SaveRefused) {
        // D10: not a failure to retry. Everything stops until the person
        // has chosen; leaving meanwhile counts as "not saved yet".
        refusedRef.current = true;
        setRefusal(_e.info);
        setState('refused');
        failedRef.current = true;
        setHasFailed(true);
        setPending(true);
        window.clearTimeout(retryTimer.current);
        return;
      }
      // D8: say so, and keep trying without being asked, at growing intervals.
      setState('failing');
      failedRef.current = true;
      setHasFailed(true);
      setPending(true);
      attempts.current += 1;
      window.clearTimeout(retryTimer.current);
      retryTimer.current = window.setTimeout(
        () => void runSave(),
        retryDelay(attempts.current)
      );
    } finally {
      window.clearTimeout(tooLong);
      window.clearTimeout(slow);
      if (inFlightRequest.current === request) inFlightRequest.current = null;
      // A save from an earlier era must not clear the mark of a newer one.
      if (era.current === mine) inFlight.current = false;
    }
  }, []);

  /*
   * What the screen holds, as words. The quiet clock below follows THIS and
   * not the value itself: the ad builder hands over a freshly built value at
   * every draw, and a screen that happened to redraw more often than every
   * two seconds (the new ad templates did, hundreds of times a second, while
   * an ad without a photo was selected; found 9 Oct 2026) restarted the
   * clock each time and was never stored.
   */
  const valuePrint =
    enabled && value !== undefined ? fingerprint(value) : undefined;

  // A change starts the quiet clock; another change restarts it.
  useEffect(() => {
    if (!enabled || valuePrint === undefined) return;

    const print = valuePrint;

    // First sight of the screen's own data is not a change.
    if (keptPrint.current === null) {
      keptPrint.current = print;
      return;
    }

    if (print === keptPrint.current) {
      /*
       * Back to what the server already has -- an undo, or retyping the old
       * words. There is nothing left to save, so the line must stop saying a
       * save is failing, which it otherwise did until some retry happened to
       * succeed.
       */
      setPending(false);
      window.clearTimeout(quietTimer.current);
      window.clearTimeout(retryTimer.current);
      attempts.current = 0;
      failedRef.current = false;
      setHasFailed(false);
      clearRefusal();
      setState(keptAt ? 'saved' : 'idle');
      return;
    }

    /*
     * The SAME words as the change already being handled, handed over as a
     * new thing: a screen that builds its value afresh at every draw (the ad
     * builder does). While a store of those words is failing, the growing
     * wait between tries is already running; taking this for a fresh edit
     * put the wait back to the start at every draw, so a store that kept
     * failing was tried every two seconds for ever (review of the brand
     * slice, 5 Oct 2026).
     */
    const sameWords = print === scheduledPrint.current;
    scheduledPrint.current = print;
    if (sameWords && failedRef.current) return undefined;

    setPending(true);
    // A fresh edit starts its own patience: otherwise the first attempt after
    // an outage inherits the one-minute wait the outage had grown to.
    attempts.current = 0;
    flushSentRef.current = false;
    window.clearTimeout(quietTimer.current);
    quietTimer.current = window.setTimeout(() => void runSave(), QUIET_MS);

    return () => window.clearTimeout(quietTimer.current);
  }, [valuePrint, enabled, runSave, keptAt]);

  // The tab going away must not cost the last words (test T5).
  useEffect(() => {
    const flushNow = () => {
      // While a refusal is on screen (D10) nothing is stored until the
      // person has chosen, also not on the way out: a reload of the refused
      // tab used to send its words after all, over the other tab's (Steven's
      // hand test of 5 Oct 2026, check 4.4).
      if (refusedRef.current) return;
      if (!pendingRef.current || flushSentRef.current) return;
      if (valueRef.current === undefined) return;
      flushSentRef.current = true;
      window.clearTimeout(quietTimer.current);
      noteSendFor(identityRef.current);
      flushOnHide?.(valueRef.current);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushNow();
        return;
      }
      /*
       * Coming BACK to the tab. The send on the way out cannot be confirmed --
       * there was no screen left to answer to -- so the screen would otherwise
       * stay convinced forever that it has unsaved work, and nothing would ever
       * clear it. Save again if anything is still unaccounted for.
       */
      flushSentRef.current = false;
      if (pendingRef.current) {
        window.clearTimeout(quietTimer.current);
        quietTimer.current = window.setTimeout(() => void runSave(), QUIET_MS);
      }
    };

    /**
     * Only a save that DID NOT GO THROUGH earns the browser's own prompt
     * (scene 12). Work still inside the quiet pause is sent above instead, in
     * silence, because the person did nothing wrong.
     */
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      /*
       * In Safari the send-on-the-way-out does not arrive when the tab closes
       * (first run in Safari's engine, 3 Oct 2026). So there, and only there,
       * the browser's own "leave this page?" prompt also appears while work
       * is still inside the pause (Steven, 4 Oct 2026). Elsewhere that work
       * is sent as the tab goes, and nobody is asked anything.
       */
      const insideThePauseInSafari = IS_SAFARI && pendingRef.current;
      const notStored = state === 'failing' || state === 'refused';
      if (!notStored && !insideThePauseInSafari) return;
      event.preventDefault();
      event.returnValue = '';
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flushNow);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushNow);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [flushOnHide, state, runSave]);

  /*
   * Leaving the SCREEN must not cost the last words either.
   *
   * Confirmed by hand on 22 Sep 2026: type, click another screen inside the
   * two-second pause, and the work was gone without a word. The send-on-the-way-
   * out only covered the tab going away; an in-app screen change unmounts this
   * hook instead, which used to throw the pending timer away and send nothing.
   */
  useEffect(
    () => () => {
      unmounted.current = true;
      window.clearTimeout(quietTimer.current);
      window.clearTimeout(retryTimer.current);
      /*
       * Not the same words twice: a save of exactly these words that is
       * already in the air survives the screen going. But words NEWER than
       * the ones in the air are sent, because nothing else will send them
       * once the screen is gone.
       */
      const value = valueRef.current;
      const sameWordsAreInTheAir =
        inFlight.current && fingerprint(value) === inFlightPrint.current;
      if (
        pendingRef.current &&
        !flushSentRef.current &&
        !sameWordsAreInTheAir &&
        // Refused words are not sent on the way out either (check 4.4).
        !refusedRef.current &&
        value !== undefined
      ) {
        flushSentRef.current = true;
        noteSendFor(identityRef.current);
        flushRef.current?.(value);
      }
      /*
       * A closed screen never retries. Its save still in the air may answer
       * later; that answer belongs to an era that ends here. If it failed,
       * the words are handed on once, and only if nothing newer went out for
       * the thing meanwhile and it is not open again (see `sends`). It used
       * to book retries from the closed screen, which sent old words on top
       * of what a screen opened later had stored (review round 10).
       */
      era.current += 1;
      // As on a campaign change: nothing of THIS screen is in the air any
      // more. Left set, a switch that was waiting on this screen waited its
      // whole patience for a save that had already answered.
      inFlight.current = false;
    },
    []
  );

  const abandon = useCallback(() => {
    // "Leave and lose changes": a save of that work still in the air is
    // stopped, so the words given up do not arrive after all, least of all
    // later, on top of newer ones (review round 10).
    inFlightRequest.current?.abort();
    noHandOver.current.add(era.current);
    abandoned.current += 1;
    era.current += 1;
    inFlight.current = false;
    window.clearTimeout(quietTimer.current);
    window.clearTimeout(retryTimer.current);
    flushSentRef.current = true;
    setPending(false);
    failedRef.current = false;
    setHasFailed(false);
    clearRefusal();
    setState('idle');
  }, []);

  const retryRefused = useCallback(async () => {
    clearRefusal();
    await runSave();
  }, [runSave]);

  const flush = useCallback(async () => {
    window.clearTimeout(quietTimer.current);
    await runSave();
  }, [runSave]);

  const saveNow = useCallback(async (): Promise<boolean> => {
    const giveUpAt = Date.now() + SAVE_NOW_PATIENCE_MS;
    const pause = (ms: number) =>
      new Promise<void>((resolve) => window.setTimeout(resolve, ms));
    const outOfPatience = () => Date.now() >= giveUpAt;

    const startedIn = era.current;
    const abandonedAtStart = abandoned.current;
    /**
     * The question this answers has gone away while it waited.
     *  - The person chose "Leave and lose changes" for something else: the
     *    switch that was waiting on this must not go ahead behind their back.
     *  - Another thing was opened, or the screen was left: the work went the
     *    way those paths send it; there is nothing left to wait for.
     */
    const settledElsewhere = (): boolean | null => {
      if (abandoned.current !== abandonedAtStart) throw new SaveNowMoot();
      if (era.current !== startedIn || unmounted.current) return true;
      return null;
    };

    /*
     * Round by round until everything is kept, or it clearly will not be:
     *  - a save already in the air is waited for, never doubled;
     *  - typing that came in while a save was in the air gets a save of its
     *    own (answering "not kept" there raised the question on a healthy
     *    line, review round 7);
     *  - a failed save is tried again, a second apart, SAVE_NOW_ATTEMPTS
     *    times in all, counted from HERE: an outage that began earlier does
     *    not use up a try (review round 8).
     */
    let failures = 0;
    let triedHere = false;
    /*
     * Refused and not chosen yet: whoever waits must drop what it was about
     * to do and ask nothing. The screen's own box is up, and that is the
     * question the person has to answer.
     */
    if (refusedRef.current) throw new SaveNowMoot();
    while (!outOfPatience()) {
      while (inFlight.current && !outOfPatience()) await pause(50);
      const elsewhere = settledElsewhere();
      if (elsewhere !== null) return elsewhere;
      if (inFlight.current) return false;
      if (!pendingRef.current) return true;
      // Nothing on screen to store (the content is not this thing's yet).
      if (valueRef.current === undefined) return true;

      if (failedRef.current && triedHere) {
        failures += 1;
        if (failures >= SAVE_NOW_ATTEMPTS) return false;
        await pause(SAVE_NOW_RETRY_GAP_MS);
        const moved = settledElsewhere();
        if (moved !== null) return moved;
        if (outOfPatience()) return false;
        if (inFlight.current) continue;
        if (!pendingRef.current) return true;
      }

      // Ours now: the pause and the hook's own retry must not also send it.
      window.clearTimeout(quietTimer.current);
      window.clearTimeout(retryTimer.current);
      triedHere = true;
      await Promise.race([
        runSave(),
        pause(Math.max(0, giveUpAt - Date.now())),
      ]);
      // Never round again without the clock moving: a save step that returns
      // at once would otherwise spin here for the whole of the patience and
      // freeze the page (measured in review round 8).
      await pause(50);
      if (refusedRef.current) throw new SaveNowMoot();
    }

    const elsewhere = settledElsewhere();
    if (elsewhere !== null) return elsewhere;
    return !pendingRef.current && !inFlight.current;
  }, [runSave]);

  const isKept = useCallback(
    () => !pendingRef.current && !inFlight.current,
    []
  );

  return {
    state,
    keptAt,
    note,
    hasPending,
    hasFailed,
    flush,
    saveNow,
    isKept,
    abandon,
    refusal,
    retryRefused,
  };
};
